import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { getAllUsersClient } from "../../actions/userAuth";
import { hasRole } from "../../utils/roleUtils";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

const columns = [
  "Admin",
  "Email",
  "Phone",
  "Role",
  "Last login",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const getInitials = (name, email) => {
  const src = name || email || "?";
  return src.charAt(0).toUpperCase();
};

const SuperAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const dispatch = useDispatch();

  const getData = () => {
    setLoading(true);
    dispatch(
      getAllUsersClient(
        { page: 1, perPage: 100000 },
        (data) => {
          const allUsers = data.data.data.users;
          const superAdmins = allUsers?.filter((user) =>
            hasRole(user, "SUPERADMIN")
          );
          dispatch({ type: "ALL_USER", payload: allUsers });
          setUserData(superAdmins || []);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  useEffect(() => {
    getData();
  }, [dispatch]);

  const stats = useMemo(
    () => ({
      total: userData.length,
      active: userData.filter((u) => u.active).length,
    }),
    [userData]
  );

  return (
    <>
      <Box
        component={motion.div}
        custom={0}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        sx={{ mb: 3 }}
      >
        <PanelCard sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ShieldOutlinedIcon sx={{ color: "#7c3aed", fontSize: 28 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Super admin accounts
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Read-only list of users with super admin role.
              </Typography>
            </Box>
          </Stack>
        </PanelCard>
      </Box>

      <Box
        component={motion.div}
        custom={1}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        sx={{ mb: 3, maxWidth: 360 }}
      >
        <StatCard
          icon={GroupsOutlinedIcon}
          label="Super admins"
          value={stats.total}
          accent="#7c3aed"
          gradient="linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)"
        />
      </Box>

      <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
        {loading ? (
          <PanelCard>
            <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
              <CircularProgress sx={{ color: "#11409B" }} />
              <Typography sx={{ mt: 2, color: "#64748b" }}>
                Loading super admins…
              </Typography>
            </Stack>
          </PanelCard>
        ) : userData?.length > 0 ? (
          <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha("#11409B", 0.04),
                    "& th": {
                      borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                    },
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "#11409B",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        py: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {userData.map((row) => (
                  <TableRow
                    key={row.id || row._id || row.email}
                    sx={{
                      transition: "background 0.2s",
                      "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                      "& td": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                        py: 1.75,
                      },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            background:
                              "linear-gradient(135deg, #7c3aed, #a78bfa)",
                            fontWeight: 700,
                            border: "2px solid #fff",
                            boxShadow: "0 4px 12px rgba(124,58,237,0.25)",
                          }}
                        >
                          {getInitials(row?.username, row?.email)}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>
                            {row?.username || "—"}
                          </Typography>
                          <Chip
                            label={row.active ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              mt: 0.5,
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: row.active
                                ? alpha("#16a34a", 0.12)
                                : alpha("#64748b", 0.12),
                              color: row.active ? "#16a34a" : "#64748b",
                            }}
                          />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        {row?.email || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#475569" }}>
                        {row?.phone ? `+91 ${row.phone}` : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row?.role || "SUPERADMIN"}
                        size="small"
                        sx={{
                          bgcolor: alpha("#7c3aed", 0.12),
                          color: "#7c3aed",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        {row?.updatedAt
                          ? dayjs(row.updatedAt).format("DD MMM YYYY")
                          : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <PanelCard>
            <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
              <ShieldOutlinedIcon
                sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                No super admins found
              </Typography>
              <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                No users with the super admin role are in the system.
              </Typography>
            </Stack>
          </PanelCard>
        )}
      </motion.div>
    </>
  );
};

export default SuperAdmin;
